import type { MutableRefObject } from "react"
import { useCallback, useEffect, useRef } from "react"

import type { QueryObserverResult } from "@tanstack/react-query"
import { differenceInMinutes } from "date-fns"
import { useLocation } from "react-router-dom"
import {
  atom,
  selector,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil"

import {
  ABANDON_DESIGN_DELAY_MINUTES,
  ABANDON_DESIGN_DELAY_MS,
  designSchema,
} from "~/containers/Proposal/state/designs/design-model"
import { useDispatchError } from "~/containers/Proposal/state/notifications/QueryErrorModal"
import {
  useCompleteDesignRequestMutation,
  useCompleteAndClaimNextDesignRequestMutation,
  useAddNoteDesignRequestMutation,
  useCreateDesignRequestMutation,
  useGetDesignerDataByRequestIdQuery,
  usePauseDesignRequestMutation,
  useRequestDesignRevisionMutation,
  useActivateDesignMutation,
  useDesignRequestClaimedUntilMutation,
  useCancelDesignRequestMutation,
  useDuplicateDesignRequestMutation,
  useEscalateDesignRequestAndClaimNextMutation,
  useRejectDesignRequestMutation,
  useGetProposalQuery,
  useEstimatesForDesignQuery,
  useEscalateDesignRequestMutation,
} from "~/graphql/client"
import type {
  CompleteAndClaimNextDesignRequestMutation,
  CompleteDesignRequestMutation,
  GetDesignerDataByRequestIdQuery,
  DesignNote,
  DesignRevisionInput,
  DesignRequestClaimedUntilMutation,
  DuplicateDesignRequestMutation,
  EscalateDesignRequestAndClaimNextMutation,
  EscalateDesignRequestMutation,
} from "~/graphql/client"
import { useGraphqlClient } from "~/hooks/graphql"
import { getPowurUrl } from "~/utils/getUrlForEnvironment"

import {
  designLocalState,
  designRemoteState,
  estimatesRemoteState,
  selectedDesignIdState,
} from "../designs/design-state"
import { designInitialLoadCompleteState } from "../designs/designInitialLoadComplete"
import { useProposalFetcher } from "../proposal/proposal-fetchers"
import {
  isProposal,
  proposalLocalState,
  proposalRemoteState,
} from "../proposal/proposal-state"
import { useFetchSiteState } from "../site/site-state"

export const designRequestState = atom<
  GetDesignerDataByRequestIdQuery[`designerDataByRequestId`] | null
>({
  key: `designRequestState`,
  default: null,
})

export const designRequestNotesState = selector<Partial<DesignNote>[]>({
  key: `designRequestNotes`,
  get: ({ get }) => {
    const { designNotes } = get(designRequestState) ?? {}
    const proposal = get(proposalLocalState)
    const design = get(designLocalState)
    const { designRequest } = proposal.designs.find(
      (d) => d.id === design.id,
    ) ?? {
      designs: [],
    }

    return designNotes ?? designRequest?.designNotes ?? []
  },
})

export const pauseDesignRequestModalIsOpenState = atom({
  key: `pauseDesignRequestModalIsOpen`,
  default: false,
})

export const useDesignerDataByRequestId = (
  designRequestId = ``,
): {
  refetch: () => Promise<
    QueryObserverResult<GetDesignerDataByRequestIdQuery, unknown>
  >
} => {
  const lastRequestId = useRef(``)
  const { client } = useGraphqlClient()
  const setDesignRequest = useSetRecoilState(designRequestState)
  const setRemote = useSetRecoilState(designRemoteState)
  const [local, setLocal] = useRecoilState(designLocalState)
  const { slug } = useRecoilValue(proposalLocalState)
  useFetchSiteState(designRequestId && slug ? slug : undefined)

  const { refetch: getProposal } = useProposalFetcher({ id: local.proposalId })
  const onError = useDispatchError()

  useEffect(() => {
    if (
      designRequestId &&
      lastRequestId.current !== designRequestId &&
      local.proposalId
    ) {
      console.count(`useDesignerDataByRequestId`)
      void getProposal()
      lastRequestId.current = designRequestId
    }
  }, [local.proposalId, getProposal, designRequestId])

  const { refetch } = useGetDesignerDataByRequestIdQuery(
    client,
    { id: designRequestId },
    {
      staleTime: Infinity,
      enabled: !!designRequestId,
      onSuccess: (data) => {
        const designParsed = designSchema.parse(
          data.designerDataByRequestId.design,
        )
        setRemote(designParsed)
        setLocal(designParsed)
        setDesignRequest(data.designerDataByRequestId)
      },
      onError,
    },
  )

  return { refetch }
}

export const useDuplicateDesignRequest = (): {
  duplicate: (
    designRequestId: string,
    message: string,
  ) => Promise<DuplicateDesignRequestMutation>
  mutation: ReturnType<typeof useDuplicateDesignRequestMutation>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const duplicateDesignRequestMutation = useDuplicateDesignRequestMutation(
    client,
    { onError },
  )

  const { mutateAsync: mutateAsyncDuplicateRevision } =
    duplicateDesignRequestMutation

  const duplicate = useCallback(
    async (designRequestId: string, message: string) => {
      const result = await mutateAsyncDuplicateRevision({
        designRequestId,
        message,
      })

      return result
    },
    [mutateAsyncDuplicateRevision],
  )

  return { duplicate, mutation: duplicateDesignRequestMutation }
}

export const useAddNoteToDesignRequest = (): {
  addNote: (designRequestId: string, message: string) => Promise<void>
  mutation: ReturnType<typeof useAddNoteDesignRequestMutation>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const mutation = useAddNoteDesignRequestMutation(client, { onError })
  const { mutateAsync: mutateAsyncAddNote } = mutation
  const addNote = useCallback(
    async (designRequestId: string, message: string) => {
      await mutateAsyncAddNote({
        id: designRequestId,
        message,
      })
    },
    [mutateAsyncAddNote],
  )

  return { addNote, mutation }
}

export const useCancelDesignRequest = (): {
  cancel: (designRequestId: string) => Promise<void>
  mutation: ReturnType<typeof useCancelDesignRequestMutation>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const mutation = useCancelDesignRequestMutation(client, { onError })
  const { mutateAsync } = mutation
  const cancel = useCallback(
    async (designRequestId: string) => {
      const result = await mutateAsync({
        id: designRequestId,
      })
    },
    [mutateAsync],
  )

  return { cancel, mutation }
}

export const useRejectDesignRequest = (): {
  reject: (designRequestId: string, message: string) => Promise<void>
  mutation: ReturnType<typeof useRejectDesignRequestMutation>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const mutation = useRejectDesignRequestMutation(client, { onError })
  const { mutateAsync } = mutation
  const reject = useCallback(
    async (designRequestId: string, message: string) => {
      const result = await mutateAsync({
        id: designRequestId,
        message,
      })
    },
    [mutateAsync],
  )

  return { reject, mutation }
}

const useSetProposal = () => useSetRecoilState(proposalRemoteState)

export const useDesignRequest = (): {
  complete: () => Promise<CompleteDesignRequestMutation | undefined>
  completeAndClaimNext: () => Promise<
    CompleteAndClaimNextDesignRequestMutation | undefined
  >
  escalate: (
    message: string,
  ) => Promise<EscalateDesignRequestMutation | undefined>
  escalateAndClaimNext: (
    message: string,
  ) => Promise<EscalateDesignRequestAndClaimNextMutation | undefined>
  create: (proposalId: string, initialMessage?: string) => Promise<void>
  pause: (url: string) => Promise<void>
  requestRevision: (data: DesignRevisionInput) => Promise<void>
  activateDesign: (designId: string) => Promise<void>
  claimUntil: (
    designRequestId: string,
  ) => Promise<DesignRequestClaimedUntilMutation | null>
} => {
  const { client } = useGraphqlClient()
  const onError = useDispatchError()
  const [designRequest, setDesignRequest] = useRecoilState(designRequestState)
  const setRemote = useSetRecoilState(designRemoteState)
  const setLoaded = useSetRecoilState(designInitialLoadCompleteState)
  const { slug } = useRecoilValue(proposalLocalState)
  const setEstimates = useSetRecoilState(estimatesRemoteState)
  const selectedDesignId = useRecoilValue(selectedDesignIdState)
  const { mutateAsync: mutateAsyncCreate } = useCreateDesignRequestMutation(
    client,
    { onError },
  )

  const setProposal = useSetProposal()

  const { refetch: refreshProposal } = useGetProposalQuery(
    client,
    { findProposalInput: { slug } },
    {
      staleTime: Infinity,
      enabled: false,
      onSuccess: (data) => {
        if (isProposal(data.proposal)) {
          setProposal(data.proposal)
        }
      },
      onError,
    },
  )

  const { mutateAsync: mutateAsyncCompleteAndClaimNext } =
    useCompleteAndClaimNextDesignRequestMutation(client, { onError })

  const { mutateAsync: mutateAsyncPauseDesignRequest } =
    usePauseDesignRequestMutation(client, { onError })

  const { mutateAsync: mutateAsyncDesignRequestClaimedUntil } =
    useDesignRequestClaimedUntilMutation(client, { onError })

  const { mutateAsync: mutateAsyncComplete } = useCompleteDesignRequestMutation(
    client,
    { onError },
  )

  const { mutateAsync: mutateAsyncRequestRevision } =
    useRequestDesignRevisionMutation(client, { onError })

  const { refetch: refetchEstimates } = useEstimatesForDesignQuery(
    client,
    { designId: selectedDesignId },
    {
      enabled: !!selectedDesignId,
      onError,
      onSuccess: (data) => {
        const { estimatesForDesign } = data
        setEstimates(estimatesForDesign)
      },
    },
  )

  const { mutateAsync: mutateAsyncActivateDesign } = useActivateDesignMutation(
    client,
    {
      onError,
      onSuccess: async ({ activateDesign }) => {
        const design = designSchema.parse(activateDesign)
        setLoaded(false)
        setRemote(design)
        await refreshProposal()
        await refetchEstimates()
      },
    },
  )

  const { mutateAsync: mutateAsyncEscalateDesignRequestAndClaimNext } =
    useEscalateDesignRequestAndClaimNextMutation(client, { onError })

  const claimUntil = useCallback(
    async (designRequestId: string) => {
      if (!designRequestId) return null
      const claimedUntil = await mutateAsyncDesignRequestClaimedUntil({
        id: designRequestId,
      })

      return claimedUntil
    },
    [mutateAsyncDesignRequestClaimedUntil],
  )

  const completeAndClaimNext = useCallback(async () => {
    if (!designRequest) {
      return
    }
    const { id } = designRequest
    try {
      const result = await mutateAsyncCompleteAndClaimNext({ id })
      setDesignRequest(null)

      return result
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === `No unclaimed design requests`
      ) {
        return undefined
      }
    }
  }, [mutateAsyncCompleteAndClaimNext, designRequest, setDesignRequest])

  const { mutateAsync: mutateAsyncEscalateDesignRequest } =
    useEscalateDesignRequestMutation(client, { onError })

  const escalate = useCallback(
    async (message: string) => {
      if (!designRequest) {
        return
      }
      const { id } = designRequest
      const result = await mutateAsyncEscalateDesignRequest({
        id,
        message,
      })
      setDesignRequest(null)

      return result
    },
    [mutateAsyncEscalateDesignRequest, designRequest, setDesignRequest],
  )

  const escalateAndClaimNext = useCallback(
    async (message: string) => {
      if (!designRequest) {
        return
      }
      const { id } = designRequest
      try {
        const result = await mutateAsyncEscalateDesignRequestAndClaimNext({
          id,
          message,
        })
        setDesignRequest(null)

        return result
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === `No unclaimed design requests`
        ) {
          return undefined
        }
      }
    },
    [
      mutateAsyncEscalateDesignRequestAndClaimNext,
      designRequest,
      setDesignRequest,
    ],
  )

  const complete = useCallback(async () => {
    if (!designRequest) {
      return
    }
    const { id } = designRequest
    const result = await mutateAsyncComplete({ id })
    setDesignRequest(null)

    return result
  }, [designRequest, setDesignRequest, mutateAsyncComplete])

  const create = useCallback(
    async (
      proposalId: string,
      initialMessage?: string,
      requestType?: string,
    ) => {
      await mutateAsyncCreate({
        proposalId,
        initialMessage: (initialMessage as string) ?? undefined,
        requestType: requestType ?? null,
      })
    },
    [mutateAsyncCreate],
  )

  const pause = useCallback(
    async (url: string) => {
      if (!designRequest) {
        return
      }
      const { id } = designRequest
      await mutateAsyncPauseDesignRequest({
        pauseDesignRequestInput: { id, url },
      })
      setDesignRequest(null)
    },
    [designRequest, mutateAsyncPauseDesignRequest, setDesignRequest],
  )

  const requestRevision = useCallback(
    async (data: DesignRevisionInput) => {
      await mutateAsyncRequestRevision({ input: data })
    },
    [mutateAsyncRequestRevision],
  )

  const activateDesign = useCallback(
    async (designId: string) => {
      await mutateAsyncActivateDesign({ id: designId })
    },
    [mutateAsyncActivateDesign],
  )

  return {
    complete,
    completeAndClaimNext,
    create,
    pause,
    requestRevision,
    activateDesign,
    claimUntil,
    escalate,
    escalateAndClaimNext,
  }
}

export const useClaimDesignRequestUntil = ({
  designRequestId,
}: {
  designRequestId: string
}): { lastActive: MutableRefObject<number> } => {
  const { pathname } = useLocation()
  const isServiceBureauRoute = pathname.includes(`service-queue`)
  const { claimUntil } = useDesignRequest()
  const lastActive = useRef(Date.now())

  useEffect(() => {
    const interval = setInterval(async () => {
      if (isServiceBureauRoute) {
        const diff = differenceInMinutes(Date.now(), lastActive.current)
        if (diff < ABANDON_DESIGN_DELAY_MINUTES) {
          await claimUntil(designRequestId)
        }
        if (diff >= ABANDON_DESIGN_DELAY_MINUTES) {
          window.location.assign(`${getPowurUrl()}/admin-solar-proposals/vision`)
        }
      }
    }, ABANDON_DESIGN_DELAY_MS)

    return () => clearInterval(interval)
  }, [claimUntil, designRequestId, isServiceBureauRoute])

  return { lastActive }
}
